import { Route } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom";
import { isPlatform, useIonToast } from "@ionic/react";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Config
import config from "../../config";

// Interfaces
import { Event } from "../../interfaces";

// API
import SettingsApi from "../../api/settings";
import AuthApi from "../../api/auth";

//Pages
import MobileHome from "./MobileHome";
import DesktopHome from "./DesktopHome";

// Store & Providers
import { setCompany } from "../../utils/localStorage";
import { useSocketStore } from "../../store/socket";
import { useSockets } from "../../providers/socket";
import { useNotification } from "../../providers/desktop-notification";
import { usePushNotifications } from "../../providers/native-push-notification";
import SupportBubble from "../../components/SupportBubble";
import { useAppStore } from "../../store/app";
import CountsStateSynchronizer from "../../providers/counts";

const version = config.VERSION;

const mobile = isPlatform("android") || isPlatform("ios");

const HOCDashboard: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { refreshUser } = props;
  const [present] = useIonToast();
  const { notify } = useNotification();
  const { setUserQuery } = useSocketStore();
  const { userSocket } = useSockets();
  const { event } = usePushNotifications();

  const setSettings = useAppStore((state) => state.setSettings);
  const settings = useAppStore((state) => state.settings);
  const user = useAppStore((state) => state.user);
  const setRole = useAppStore((state) => state.setRole);
  const hideSupportBubble =
    location.pathname.startsWith("/welcome") ||
    location.pathname.startsWith("/ai/playground/welcome");

  const getSettings = async () => {
    const { data, err } = await SettingsApi.get();
    if (data) {
      setSettings(data.settings);
    }
    if (err) {
      setSettings({});
    }
  };

  const switchCompany = async ({ companyId }: { companyId: string }) => {
    const { data, err } = await AuthApi.switchCompany({ companyId });
    if (data) {
      const { company } = data.user;
      await setCompany({ company });
      window.location.reload();
    }
    if (err) {
      return present({
        message: "Something went wrong",
        duration: 5000,
        color: "danger",
        position: "top",
        cssClass: "error-toast",
      });
    }
  };

  useEffect(() => {
    const query = {
      email: user.email,
      companyId: user.company?.id,
      ...version,
      mobile,
    };
    setUserQuery(query);
  }, [user.email, user.company?.id]);

  useEffect(() => {
    getSettings();
  }, []);

  useEffect(() => {
    if (!userSocket) return;

    const handleCompanyMessage = (message: any) => {
      console.log("new company message...", message);
      switch (message.data?.task) {
        case "settings":
          if (settings.company?.id === message?.companyId) {
            console.log("refreshing settings store.....");
            console.log(message.data.message);
            setSettings(message.data.message);
          }
          break;
      }
    };

    const handleUserMessage = (message: any) => {
      console.log("new user message...", message);
      switch (message.data?.task) {
        case "role":
          console.log("updating user role in store.....");
          const { role } = message.data.message;
          setRole(role);
          break;
      }
    };

    // Desktop notifications handling of action performed
    const handleDesktopNotification = (message: any) => {
      console.log("new notification...");
      try {
        queryClient.refetchQueries({
          queryKey: ["getCounts"],
          type: "active",
          exact: true,
        });
        const { data } = message;
        const { notification, payload } = data;
        notify({
          title: notification.title,
          body: notification.body,
          payload,
          sound: true,
          onClick: () => {
            handleActionPeformed({
              timeStamp: new Date().toString(),
              data: payload,
              type: "chat",
              companyId: `undefined`,
            });
          },
        });
      } catch (e) {
        console.error(e);
      }
    };

    userSocket.on("company-message", handleCompanyMessage);
    userSocket.on("user-message", handleUserMessage);
    userSocket.on("not-escalation", handleDesktopNotification);
    userSocket.on("not-new-message", handleDesktopNotification);
    return () => {
      userSocket.off("company-message");
      userSocket.off("not-escalation", handleDesktopNotification);
      userSocket.off("not-new-message", handleDesktopNotification);
    };
  }, [userSocket, settings.company?.id, notify]);

  // Push notifications handling of action performed
  useEffect(() => {
    if (event) {
      handleActionPeformed(event);
    }
  }, [event]);

  const handleActionPeformed = async (event: Event) => {
    // This handles an action performed with push or desktop event and redirects the user to the appropriate page
    // Example, we send a push notification with a chat id, and we redirect the user to the chat page
    switch (event.type) {
      case "chat":
        mobile && event.data && history.push(`/chat/${event.data.id}`);
        !mobile && event.data && history.push(`/chats/${event.data.id}`);
        break;
    }
  };

  return mobile ? (
    <>
      <CountsStateSynchronizer />
      <Route
        path="/"
        render={(props) => (
          <>
            <MobileHome
              {...props}
              refreshUser={refreshUser}
              onRequestSettings={() => getSettings()}
              switchCompany={switchCompany}
            />
          </>
        )}
      />
    </>
  ) : (
    <>
      <CountsStateSynchronizer />
      {!hideSupportBubble ? <SupportBubble /> : null}
      <Route
        path="/"
        render={(props) => (
          <DesktopHome
            {...props}
            refreshUser={refreshUser}
            switchCompany={switchCompany}
            onRequestSettings={() => getSettings()}
          />
        )}
      />
    </>
  );
};

interface ILayoutProps {
  refreshUser: Function;
}

export default HOCDashboard;
